import { ZipcodeMetrics, MetricType, ColorScale } from './types';

// Color schemes for different metrics
export const COLOR_SCHEMES = {
  blue: ['#EBF8FF', '#BEE3F8', '#90CDF4', '#63B3ED', '#4299E1', '#3182CE', '#2D72C7'],
  green: ['#F0FDF4', '#DCFCE7', '#BBF7D0', '#86EFAC', '#4ADE80', '#22C55E', '#16A34A'],
  red: ['#FEF2F2', '#FECACA', '#FCA5A5', '#F87171', '#EF4444', '#DC2626', '#B91C1C'],
  purple: ['#FAF5FF', '#E9D5FF', '#D8B4FE', '#C084FC', '#A855F7', '#9333EA', '#7C3AED'],
  orange: ['#FFF7ED', '#FFEDD5', '#FED7AA', '#FDBA74', '#FB923C', '#F97316', '#EA580C'],
};

// Default metric colors
export const METRIC_COLORS: Record<MetricType, keyof typeof COLOR_SCHEMES> = {
  clicks: 'blue',
  impressions: 'green',
  spend: 'red',
  conversions: 'purple',
  ctr: 'orange',
  cpc: 'blue',
  cpm: 'green',
  cpa: 'red',
};

// Calculate metric value for a zipcode
export const getMetricValue = (zipcode: ZipcodeMetrics, metric: MetricType): number => {
  let value: number;

  switch (metric) {
    case 'clicks':
      value = zipcode.clicks;
      break;
    case 'impressions':
      value = zipcode.impressions;
      break;
    case 'spend':
      value = zipcode.spend;
      break;
    case 'conversions':
      value = zipcode.conversions;
      break;
    case 'ctr':
      value = zipcode.ctr || (zipcode.impressions > 0 ? (zipcode.clicks / zipcode.impressions) * 100 : 0);
      break;
    case 'cpc':
      value = zipcode.cpc || (zipcode.clicks > 0 ? zipcode.spend / zipcode.clicks : 0);
      break;
    case 'cpm':
      value = zipcode.cpm || (zipcode.impressions > 0 ? (zipcode.spend / zipcode.impressions) * 1000 : 0);
      break;
    case 'cpa':
      value = zipcode.cpa || (zipcode.conversions > 0 ? zipcode.spend / zipcode.conversions : 0);
      break;
    default:
      value = 0;
  }

  // Ensure we never return NaN or Infinity
  return isNaN(value) || !isFinite(value) ? 0 : value;
};

// Calculate color scale for a metric across all zipcodes
export const calculateColorScale = (data: ZipcodeMetrics[], metric: MetricType): ColorScale => {
  const values = data.map(zipcode => getMetricValue(zipcode, metric)).filter(value =>
    value > 0 && !isNaN(value) && isFinite(value)
  );

  if (values.length === 0) {
    return { min: 0, max: 1, colors: COLOR_SCHEMES.blue }; // Use max: 1 to prevent division by zero
  }

  const min = Math.min(...values);
  const max = Math.max(...values);
  const colorScheme = METRIC_COLORS[metric];

  return {
    min,
    max: Math.max(max, 1), // Ensure max is at least 1 to prevent division by zero
    colors: COLOR_SCHEMES[colorScheme],
  };
};

// Get color for a specific value based on the color scale
export const getColorForValue = (value: number, colorScale: ColorScale): string => {
  if (value === 0 || colorScale.max === colorScale.min) {
    return colorScale.colors[0]; // Lightest color for zero values
  }

  const normalizedValue = (value - colorScale.min) / (colorScale.max - colorScale.min);
  const colorIndex = Math.min(
    Math.floor(normalizedValue * (colorScale.colors.length - 1)),
    colorScale.colors.length - 1
  );

  return colorScale.colors[colorIndex];
};

// Get opacity for a specific value (for heatmap intensity)
export const getOpacityForValue = (value: number, colorScale: ColorScale, minOpacity: number = 0.3, maxOpacity: number = 0.9): number => {
  if (value === 0 || colorScale.max === colorScale.min) {
    return minOpacity;
  }

  const normalizedValue = (value - colorScale.min) / (colorScale.max - colorScale.min);
  return minOpacity + (normalizedValue * (maxOpacity - minOpacity));
};

// Format metric value for display
export const formatMetricValue = (value: number, metric: MetricType): string => {
  switch (metric) {
    case 'spend':
    case 'cpc':
    case 'cpm':
    case 'cpa':
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      }).format(value);
    case 'ctr':
      return `${value.toFixed(2)}%`;
    case 'clicks':
    case 'impressions':
    case 'conversions':
      return new Intl.NumberFormat('en-US').format(Math.round(value));
    default:
      return value.toFixed(2);
  }
};

// Get display name for metric
export const getMetricDisplayName = (metric: MetricType): string => {
  const names: Record<MetricType, string> = {
    clicks: 'Clicks',
    impressions: 'Impressions',
    spend: 'Spend',
    conversions: 'Conversions',
    ctr: 'Click-Through Rate',
    cpc: 'Cost Per Click',
    cpm: 'Cost Per Mille',
    cpa: 'Cost Per Acquisition',
  };
  return names[metric];
};

// Re-export geocoding functions from the service
export {
  getZipcodeCoordinates,
  processZipcodeData,
  batchGeocodeZipcodes,
  clearGeocodeCache,
  getGeocodeStats,
} from './geocoding.service';
